import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { Formula } from "building-types/options/formula";
import type { ProfileId } from "building-types/profiles/profileTypes";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { friezeBoardNew } from "building-types/roofs/roofTrimTypes";
import type { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { EvaluatedRoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import {
  EaveBracketPlacement,
  EaveBracketSide,
  type FriezeBoard,
  type RoofTruncationType,
} from "building-types/settings/roofBaseTypes";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type {
  EvaluatedRoofEdgeOverrides,
  RoofEdgeOverrides,
  RoofEdgeOverridesTable,
  RoofGableVents,
} from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { defaultRoofFasciaTrimOverrides, roofMaterialAndTrimPropsNew } from "doc/utils/roofTools";
import { DIST_EPSILON } from "geom/core";
import { planeNew } from "geom/plane";
import { polyLine2EdgeTangent, polyLine2Start } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { includeElementBasedOnOrientation } from "../multiGableRoofTools";

export function friezeBoardRoofSettingsForTheSide(
  roofSettings: RoofSettings,
  isRakeSide: boolean,
  sideAlignsWithFriezeBoardOrientationInRoofSettings: boolean,
  includeFriezeBoardLegacy?: boolean | ProfileId,
) {
  let friezeBoardSettingFromRoofSettings = {};
  if (sideAlignsWithFriezeBoardOrientationInRoofSettings || includeFriezeBoardLegacy) {
    let friezeBoard: FriezeBoard[] | undefined = undefined;
    if (typeof includeFriezeBoardLegacy === "string" && sideAlignsWithFriezeBoardOrientationInRoofSettings) {
      friezeBoard = [friezeBoardNew(includeFriezeBoardLegacy, "Frieze Board 1")];
    } else if (typeof includeFriezeBoardLegacy === "boolean" && includeFriezeBoardLegacy) {
      if (isRakeSide && roofSettings.rakeFriezeBoard) {
        friezeBoard = [...roofSettings.rakeFriezeBoard];
      } else if (!isRakeSide && roofSettings.eaveFriezeBoard) {
        friezeBoard = [...roofSettings.eaveFriezeBoard];
      }
    } else if (sideAlignsWithFriezeBoardOrientationInRoofSettings) {
      friezeBoard = isRakeSide ? roofSettings.rakeFriezeBoard : roofSettings.eaveFriezeBoard;
    }
    friezeBoardSettingFromRoofSettings = {
      friezeBoard,
      wrapFriezeBoardMode: isRakeSide ? roofSettings.wrapFriezeBoardMode : undefined,
      friezeBoardMaterialLegacy: isRakeSide
        ? roofSettings.rakeFriezeBoardMaterialLegacy
        : roofSettings.eaveFriezeBoardMaterialLegacy,
    };
  }
  return friezeBoardSettingFromRoofSettings;
}

export function getRakeBracketRoofSettingsForTheSide(
  roofSettings: RoofSettings,
  side: RoofSide,
  sideAlignsWithFront: boolean,
  includeRakeBrackets?: boolean,
) {
  let rakeBracketSettingFromRoofSettings = {};
  if (side !== RoofSide.START && side !== RoofSide.END) {
    return rakeBracketSettingFromRoofSettings;
  }
  if (sideAlignsWithFront || includeRakeBrackets) {
    rakeBracketSettingFromRoofSettings = {
      includeRakeBrackets: sideAlignsWithFront,
      rakeBracketPropId: roofSettings.rakeBracketPropId,
      rakeBracketAtPeak: roofSettings.rakeBracketAtPeak,
      rakeBracketParams: roofSettings.rakeBracketParams,
      rakeBracketParamsNormalized: roofSettings.rakeBracketParamsNormalized,
      rakeBracketTruncationParams: roofSettings.rakeBracketTruncationParams,
    };
  }
  return rakeBracketSettingFromRoofSettings;
}

export function getEaveBracketRoofSettingsForTheSide(
  roofSettings: RoofSettings,
  side: RoofSide,
  includeEaveBrackets: boolean | undefined,
  isTruncated: boolean,
  isSideFullyTruncated: boolean | undefined,
  isRake: boolean,
) {
  const { eaveBracketPropId } = roofSettings;
  if ((!isSideFullyTruncated && isTruncated) || isRake) {
    return {
      includeEaveBrackets: undefined,
      eaveBracketPropId: undefined,
      eaveBracketParams: undefined,
      eaveBracketParamsNormalized: undefined,
      eaveBracketPlacement: undefined,
    };
  }
  if (!includeEaveBrackets && !eaveBracketPropId) {
    return {};
  }
  let includeEaveBracketsFromRoofSettings: boolean = false;
  if (
    roofSettings.eaveBracketSide === EaveBracketSide.ALL ||
    (roofSettings.eaveBracketSide === EaveBracketSide.BOTH && (side === RoofSide.LEFT || side === RoofSide.RIGHT)) ||
    (side === RoofSide.LEFT && roofSettings.eaveBracketSide === EaveBracketSide.LEFT) ||
    (side === RoofSide.RIGHT && roofSettings.eaveBracketSide === EaveBracketSide.RIGHT)
  ) {
    includeEaveBracketsFromRoofSettings = true;
  }
  if (includeEaveBrackets ?? includeEaveBracketsFromRoofSettings) {
    return {
      includeEaveBrackets: true,
      eaveBracketPropId: roofSettings.eaveBracketPropId ?? BuiltInExteriorPropId.RAKEBRACKET,
      eaveBracketParams: roofSettings.eaveBracketParams ?? [0.5],
      eaveBracketParamsNormalized: roofSettings.eaveBracketParamsNormalized ?? true,
      eaveBracketPlacement: roofSettings.eaveBracketPlacement ?? EaveBracketPlacement.START_TO_END,
    };
  }
  return {};
}

interface HeightOffsetSettingsForTheSideResult {
  heelHeightOffset: Formula | number | undefined;
  normalizedHeelHeightOffset: boolean | undefined;
  truncationType: RoofTruncationType | undefined;
}

function getHeightOffsetSettingsForTheSide(
  roofSettings: RoofSettings,
  side: RoofSide,
  roofSideOverrides?: EvaluatedRoofEdgeOverrides | RoofEdgeOverrides,
): HeightOffsetSettingsForTheSideResult {
  let truncationType: RoofTruncationType | undefined;
  let normalizedHeelHeightOffset: boolean | undefined;
  let heelHeightOffset: number | Formula | undefined = roofSettings.eaveFasciaHeightOffset;

  switch (side) {
    case RoofSide.LEFT:
      heelHeightOffset =
        roofSideOverrides?.heelHeightOffset ?? roofSettings.eaveLeftFasciaHeightOffsetOverride ?? heelHeightOffset;
      break;
    case RoofSide.RIGHT:
      heelHeightOffset =
        roofSideOverrides?.heelHeightOffset ?? roofSettings.eaveRightFasciaHeightOffsetOverride ?? heelHeightOffset;
      break;
    case RoofSide.START:
      truncationType = roofSettings.startTruncationType;
      normalizedHeelHeightOffset =
        roofSideOverrides?.normalizedHeelHeightOffset ?? roofSettings.startTruncationNormalizedHeight;
      heelHeightOffset = roofSideOverrides?.heelHeightOffset ?? roofSettings.startTruncationHeightFactor;
      break;
    case RoofSide.END:
      truncationType = roofSettings.endTruncationType;
      normalizedHeelHeightOffset =
        roofSideOverrides?.normalizedHeelHeightOffset ?? roofSettings.endTruncationNormalizedHeight;
      heelHeightOffset = roofSideOverrides?.heelHeightOffset ?? roofSettings.endTruncationHeightFactor;
      break;
    default:
      // do nothing
      break;
  }

  return {
    heelHeightOffset,
    normalizedHeelHeightOffset,
    truncationType,
  };
}

function getSlopeSettingsForTheSide(roofSettings: RoofSettings, roofSide: RoofEdgeOverrides = {}) {
  return {
    slope: roofSide.slope ?? roofSettings.slope,
    slopeOptions: roofSide.slopeOptions ?? roofSettings.slopes,
  };
}

function mergeRoofEdgeOverrides<EvaluatedRoofEdgeOverrides>(
  parent: EvaluatedRoofEdgeOverrides,
  child: EvaluatedRoofEdgeOverrides,
): EvaluatedRoofEdgeOverrides;
function mergeRoofEdgeOverrides(parent: RoofEdgeOverrides, child: RoofEdgeOverrides): RoofEdgeOverrides {
  const filteredInstanceChild: Partial<RoofEdgeOverrides> = {};

  for (const id in child) {
    const val = child[id];

    if (val !== undefined && val !== null) {
      filteredInstanceChild[id] = val;
    }
  }

  return {
    ...parent,
    ...filteredInstanceChild,
  };
}

export function resolvedRoofSideParams(
  roofSettings: RoofSettings,
  side: RoofSide,
  sideAlignsWithFriezeBoardOrientationInRoofSettings: boolean,
  sideAlignsWithFront: boolean,
  isRake: boolean,
  roofSide?: EvaluatedRoofEdgeOverrides,
  isFullyTruncatedEnd?: boolean,
): EvaluatedRoofEdgeOverrides;
export function resolvedRoofSideParams(
  roofSettings: RoofSettings,
  side: RoofSide,
  sideAlignsWithFriezeBoardOrientationInRoofSettings: boolean,
  sideAlignsWithFront: boolean,
  isRake: boolean,
  roofSide?: RoofEdgeOverrides,
  isFullyTruncatedEnd?: boolean,
): RoofEdgeOverrides {
  const friezeBoardSettingFromRoofSettings = friezeBoardRoofSettingsForTheSide(
    roofSettings,
    isRake,
    sideAlignsWithFriezeBoardOrientationInRoofSettings,
    roofSide?.includeFriezeBoardLegacy,
  );

  const rakeBracketsFromRoofSettings = getRakeBracketRoofSettingsForTheSide(
    roofSettings,
    side,
    sideAlignsWithFront,
    roofSide?.includeRakeBrackets,
  );

  const eaveBracketsFromRoofSettings = getEaveBracketRoofSettingsForTheSide(
    roofSettings,
    side,
    roofSide?.includeEaveBrackets,
    isRoofEdgeTruncated(roofSide),
    isFullyTruncatedEnd,
    isRake,
  );

  const { truncationType, heelHeightOffset, normalizedHeelHeightOffset } = getHeightOffsetSettingsForTheSide(
    roofSettings,
    side,
    roofSide,
  );
  const { slope, slopeOptions } = getSlopeSettingsForTheSide(roofSettings, roofSide);

  const isStartOrEnd: boolean = side === RoofSide.START || side === RoofSide.END;

  const roofSettingsForRoofSide: RoofEdgeOverrides = {
    ...defaultRoofFasciaTrimOverrides,
    includeTriangleGableVent: roofSettings.includeTriangleGableVent,
    triangleGableVentBaseWidth: roofSettings.triangleGableVentBaseWidth,
    gableBellyBandBoard: isStartOrEnd ? roofSettings.gableBellyBandBoard : undefined,
    gableBellyBandBoardMaterial: isStartOrEnd ? roofSettings.gableBellyBandBoardMaterial : undefined,
    gableBellyBandBoardAnchor: isStartOrEnd ? roofSettings.gableBellyBandBoardAnchor : undefined,
    gableBellyBandBoardJustification: isStartOrEnd ? roofSettings.gableBellyBandBoardJustification : undefined,
    gableBellyBandBoardOffset: isStartOrEnd ? roofSettings.gableBellyBandBoardOffset : undefined,
    includeFasciaTopTrim: roofSettings.includeFasciaTopTrim,
    fasciaTopTrimSize: roofSettings.fasciaTopTrimSize,
    fasciaTopTrimMaterial: roofSettings.fasciaTopTrimMaterial,
    fasciaSize: isRake ? roofSettings.rakeFasciaSize : roofSettings.eaveFasciaSize,
    fasciaMaterial: isRake ? roofSettings.rakeFasciaMaterial : roofSettings.eaveFasciaMaterial,
    soffitOffset: roofSettings.soffitOffset,
    soffitMaterial: roofSettings.soffitMaterial,
    eaveCutType: roofSettings.eaveCutType,
    ...friezeBoardSettingFromRoofSettings,
    ...rakeBracketsFromRoofSettings,
    ...eaveBracketsFromRoofSettings,
    slope: slope,
    slopeOptions: slopeOptions as EvaluatedRoofSlopeOptions[] | undefined,
    truncationType,
    heelHeightOffset,
    normalizedHeelHeightOffset,
  };

  //copy venting - also expand to do others here as needed
  if (roofSide) {
    return mergeRoofEdgeOverrides(roofSettingsForRoofSide, roofSide);
  }

  return roofSettingsForRoofSide;
}

export function resolvedRoofEdgeParams(
  roofSettings: RoofSettings,
  side: RoofSide,
  edgeAlignsWithFriezeBoardOrientationInRoofSettings: boolean,
  isRake: boolean,
  roofEdge?: EvaluatedRoofEdgeOverrides,
  isFullyTruncatedEnd?: boolean,
): EvaluatedRoofEdgeOverrides;
export function resolvedRoofEdgeParams(
  roofSettings: RoofSettings,
  side: RoofSide,
  edgeAlignsWithFriezeBoardOrientationInRoofSettings: boolean,
  isRake: boolean,
  roofEdge?: RoofEdgeOverrides,
  isFullyTruncatedEnd?: boolean,
): RoofEdgeOverrides {
  const isStartOrEnd = side === RoofSide.START || side === RoofSide.END;
  const friezeBoardSettingFromRoofSettings = friezeBoardRoofSettingsForTheSide(
    roofSettings,
    isRake,
    edgeAlignsWithFriezeBoardOrientationInRoofSettings,
    roofEdge?.includeFriezeBoardLegacy,
  );
  const eaveBracketsFromRoofSettings = getEaveBracketRoofSettingsForTheSide(
    roofSettings,
    side,
    roofEdge?.includeEaveBrackets,
    isRoofEdgeTruncated(roofEdge),
    isFullyTruncatedEnd,
    isRake,
  );
  const { truncationType, heelHeightOffset, normalizedHeelHeightOffset } = getHeightOffsetSettingsForTheSide(
    roofSettings,
    side,
    roofEdge,
  );

  const roofSettingsForRoofEdge: RoofEdgeOverrides = {
    ...defaultRoofFasciaTrimOverrides,
    includeFasciaTopTrim: roofSettings.includeFasciaTopTrim,
    fasciaTopTrimSize: roofSettings.fasciaTopTrimSize,
    gableBellyBandBoard: isStartOrEnd ? roofSettings.gableBellyBandBoard : undefined,
    gableBellyBandBoardAnchor: isStartOrEnd ? roofSettings.gableBellyBandBoardAnchor : undefined,
    gableBellyBandBoardJustification: isStartOrEnd ? roofSettings.gableBellyBandBoardJustification : undefined,
    gableBellyBandBoardOffset: isStartOrEnd ? roofSettings.gableBellyBandBoardOffset : undefined,
    fasciaTopTrimMaterial: roofSettings.fasciaTopTrimMaterial,
    fasciaSize: isRake ? roofSettings.rakeFasciaSize : roofSettings.eaveFasciaSize,
    fasciaMaterial: isRake ? roofSettings.rakeFasciaMaterial : roofSettings.eaveFasciaMaterial,
    soffitOffset: roofSettings.soffitOffset,
    soffitMaterial: roofSettings.soffitMaterial,
    eaveCutType: roofSettings.eaveCutType,
    ...friezeBoardSettingFromRoofSettings,
    ...eaveBracketsFromRoofSettings,
    truncationType,
    heelHeightOffset,
    normalizedHeelHeightOffset,
  };

  if (roofEdge) {
    return mergeRoofEdgeOverrides(roofSettingsForRoofEdge, roofEdge);
  }

  return roofSettingsForRoofEdge;
}

export function resolvedGableVent(roofSettings: RoofSettings, sideOverrides?: RoofEdgeOverridesTable): RoofGableVents {
  let gableVents: RoofGableVents = {};
  const resolvedStartRoofSideOverride = resolvedRoofSideParams(
    roofSettings,
    RoofSide.START,
    false,
    false,
    false,
    sideOverrides ? sideOverrides[RoofSide.START] : undefined,
  );
  const includeStartTriangleGableVent = resolvedStartRoofSideOverride.includeTriangleGableVent;
  if (includeStartTriangleGableVent) {
    const startTriangleGableVentBaseWidth = resolvedStartRoofSideOverride.triangleGableVentBaseWidth;
    gableVents = { ...gableVents, includeStartTriangleGableVent, startTriangleGableVentBaseWidth };
  }
  const resolvedEndRoofSideOverride = resolvedRoofSideParams(
    roofSettings,
    RoofSide.END,
    false,
    false,
    false,
    sideOverrides ? sideOverrides[RoofSide.END] : undefined,
  );
  const includeEndTriangleGableVent = resolvedEndRoofSideOverride.includeTriangleGableVent;
  if (includeEndTriangleGableVent) {
    const endTriangleGableVentBaseWidth = resolvedEndRoofSideOverride.triangleGableVentBaseWidth;
    gableVents = { ...gableVents, includeEndTriangleGableVent, endTriangleGableVentBaseWidth };
  }
  return gableVents;
}

export function resolvedRoofMaterialTrimAndProps(
  edge: PolyLine2,
  edgeOverride: EvaluatedRoofEdgeOverrides,
  roofSettings: RoofSettings,
  isRake: boolean,
  side: RoofSide,
  isFullyTruncatedEnd: boolean,
) {
  const { friezeBoardOrientation = StreetOrientationMode.FRONT_AND_SIDE_FACES } = roofSettings;

  const edgeAlignsWithFriezeBoardOrientationInRoofSettings: boolean = doesEdgeAlignsWithInputOrientation(
    edge,
    friezeBoardOrientation,
  );

  const edgeAlignsWithFront: boolean = doesEdgeAlignsWithInputOrientation(edge, StreetOrientationMode.FRONT_FACES);

  const resolvedRoofSideOverride = resolvedRoofSideParams(
    roofSettings,
    side,
    edgeAlignsWithFriezeBoardOrientationInRoofSettings,
    edgeAlignsWithFront,
    isRake,
    edgeOverride,
    isFullyTruncatedEnd,
  );

  const resolvedRoofEdgeOverride = resolvedRoofEdgeParams(
    roofSettings,
    side,
    edgeAlignsWithFriezeBoardOrientationInRoofSettings,
    isRake,
    edgeOverride,
    isFullyTruncatedEnd,
  );

  const {
    includeFasciaTopTrim,
    fasciaTopTrimSize,
    fasciaTopTrimMaterial,
    friezeBoard,
    friezeBoardMaterialLegacy,
    wrapFriezeBoardMode,
    includeRakeBrackets,
    rakeBracketPropId,
    rakeBracketAtPeak,
    rakeBracketParams,
    rakeBracketParamsNormalized,
    rakeBracketTruncationParams,
    soffitMaterial,
    fasciaMaterial,
    fasciaSize,
    soffitOffset,
    includeEaveBrackets,
    eaveBracketPropId,
    eaveBracketParams,
    eaveBracketParamsNormalized,
    eaveBracketPlacement,
  } = { ...resolvedRoofSideOverride, ...resolvedRoofEdgeOverride };

  return roofMaterialAndTrimPropsNew({
    includeFasciaTopTrim,
    fasciaTopTrimSize,
    fasciaTopTrimMaterial,
    friezeBoard,
    friezeBoardMaterialLegacy,
    wrapFriezeBoardMode,
    includeRakeBrackets,
    rakeBracketPropId,
    rakeBracketAtPeak,
    rakeBracketParams,
    rakeBracketParamsNormalized,
    rakeBracketTruncationParams,
    soffitMaterial,
    fasciaMaterial,
    fasciaSize,
    soffitOffset,
    includeEaveBrackets,
    eaveBracketPropId,
    eaveBracketParams,
    eaveBracketParamsNormalized,
    eaveBracketPlacement,
  });
}

export function resolveRoofWallFramingType(
  roofFraminSettings: RoofFramingSettings,
  edgeOverride: RoofEdgeOverrides,
  side: RoofSide,
): RoofWallFramingType | undefined {
  switch (side) {
    case RoofSide.START:
      return edgeOverride.framingType ?? roofFraminSettings.startFramingType;
    case RoofSide.END:
      return edgeOverride.framingType ?? roofFraminSettings.endFramingType;
    default:
      return edgeOverride.framingType ?? roofFraminSettings.eaveFramingType;
  }
}

export function doesEdgeAlignsWithInputOrientation(edge: PolyLine2, orientation: StreetOrientationMode) {
  const [startX, startY] = polyLine2Start(edge);
  const [dirX, dirY] = polyLine2EdgeTangent(edge, 0);
  const edgePlane = planeNew(startX, startY, 0, dirX, dirY, 0, 0, 0, 1);
  return includeElementBasedOnOrientation(edgePlane, orientation, DIST_EPSILON);
}

export function isRoofEdgeTruncated(roofEdge?: RoofEdgeOverrides): boolean {
  if (!roofEdge) {
    return false;
  }

  const { heelHeightOffset } = roofEdge;

  if (heelHeightOffset === undefined || heelHeightOffset === null || typeof heelHeightOffset !== "number") {
    return false;
  }

  return heelHeightOffset > DIST_EPSILON;
}
